import React, { useEffect, useState, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import {
  initSentry,
  getAccessToken,
  removeAccessToken,
  saveAccessToken,
  useAppDispatch,
  useAppSelector,
  setAppLoading,
  setLoginState,
  setUser,
  setWallets,
  setCurrentApp,
  setIsSkipOnBoarding,
  setShowOnboarding,
  selectIsLoggedIn,
  selectShowOnboarding,
  registerAuthCallbacks,
  logout,
  selectIsAppLoading,
  getUserProfile,
  checkVisitor,
  authService,
  useSubscriptionExpiredModal,
  useProjectArchiveModal,
  useGracePeriodModal,
} from '@ideoz/core';
import { IdeozLoading, Toaster, SmartPlanExpiredModal, ProjectArchiveModal, GracePeriodModal, showToast } from '@ideoz/ui';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  CanvasExportDemo,
  ConversationPage,
  LandingPage,
  MyProfile,
  Billing,
  OAuth2Callback,
  Onboarding,
  ProjectDetails,
  UserGreating,
  PublishedPrototype,
  ProjectListPage,
  Register,
  Redirect,
  Login,
  ForgotPassword,
  ResetPassword,
} from './pages';
import { useWebPageTracking, useClickTracking } from '@ideoz/core/analytics';
import { useGoogleOneTap } from './hooks/useGoogleOneTap';
import { GOOGLE_AUTH_CONFIG } from './config/googleAuth';
import { useWebNavigation } from './hooks/useWebNavigation';
import { MobileOnlyModal } from '@ideoz/ui';
import { isMobile } from 'react-device-detect';

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const isAppLoading = useAppSelector(selectIsAppLoading);

  // Wait for token validation to complete before redirecting
  // This handles cases where user navigates with ?token= parameter (e.g., from plugin)
  if (isAppLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <IdeozLoading size="lg" />
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/landing" replace />;
  }

  return <>{children}</>;
};

// Root Route - always shows landing page
// Onboarding redirect is handled in validateToken function
const RootRoute = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const showOnboarding = useAppSelector(selectShowOnboarding);
  const isAppLoading = useAppSelector(selectIsAppLoading);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const previousPathnameRef = useRef<string>(location.pathname);

  // Reset showOnboarding if user navigates back to root from /onboarding
  // This prevents showing onboarding when user navigates back to landing page
  // IMPORTANT: This useEffect must be called before any conditional returns to follow Rules of Hooks
  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;

    // If user navigated from /onboarding to /, reset showOnboarding immediately
    if (location.pathname === '/' && previousPathname === '/onboarding' && showOnboarding) {
      console.log('Resetting showOnboarding - user navigated back to root from onboarding');
      dispatch(setShowOnboarding(false));
      // Also silently update the API flag to prevent future redirects
      if (isLoggedIn) {
        authService.updateOnboardingShown().catch((error) => {
          console.error('Failed to update onboarding shown flag on navigation back:', error);
        });
      }
    }
  }, [location.pathname, showOnboarding, dispatch, isLoggedIn]);

  // Show loading while app is initializing
  if (isAppLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <IdeozLoading size="lg" />
      </div>
    );
  }

  console.log('RootRoute render:', { showOnboarding, isLoggedIn, pathname: location.pathname });

  // Always show LandingPage on root route '/'
  // Onboarding redirect is handled in validateToken when user logs in with isOnBoardingShown === false
  return <LandingPage />;
};

// App Content Component (needs to be inside BrowserRouter to use navigate)
function AppContent() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const showOnboarding = useAppSelector(selectShowOnboarding);
  const planDetails = useAppSelector((state: any) => state.app.planDetails);
  const user = useAppSelector((state: any) => state.app.user);
  const [checkingToken, setCheckingToken] = useState(true);
  const hasInitialized = useRef(false);
  const visitorCheckCalled = useRef(false);
  const callbacksRegistered = useRef(false);
  const onboardingRedirected = useRef(false); // Track if we've already redirected to onboarding

  // Navigation for modals
  const navigation = useWebNavigation();

  // Toast helper for modals
  const toastHelper = useCallback((options: { message: string; severity: 'success' | 'error' | 'info' | 'warning' }) => {
    showToast({
      message: options.message,
      severity: options.severity,
    });
  }, []);

  // Project archive modal state (must be declared before subscription expired modal)
  // Web: Don't provide onUpgrade - hook will use built-in Stripe checkout flow
  const projectArchiveModal = useProjectArchiveModal({
    navigation,
    planDetails,
    showToast: toastHelper,
    // onUpgrade not provided - uses Stripe checkout on web
  });

  // Subscription expired modal state
  // Web: Don't provide onRenew - hook will use built-in Stripe checkout flow
  const subscriptionExpiredModal = useSubscriptionExpiredModal({
    navigation,
    planDetails,
    showToast: toastHelper,
    onShowProjectArchiveModal: projectArchiveModal.openModal,
    // onRenew not provided - uses Stripe checkout on web
  });

  // Grace period modal state
  // Shows for users with 2+ projects who haven't seen the notice yet
  const gracePeriodModal = useGracePeriodModal({
    navigation,
    planDetails,
    user,
    showToast: toastHelper,
    // onViewPlans not provided - uses navigation.toBilling on web
  });

  // Analytics — auto page-view & click tracking
  useWebPageTracking(location.pathname);
  useClickTracking();

  // Production-only: SPA page view tracking for Google Analytics and Meta Pixel
  useEffect(() => {
    if (import.meta.env.MODE !== 'production') return;
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'page_view', { page_path: location.pathname });
    }
    if (typeof (window as any).fbq === 'function') {
      (window as any).fbq('track', 'PageView');
    }
  }, [location.pathname]);

  // Initialize Google One Tap (shows "Continue as..." popup)
  // Disable on login/register pages to avoid conflicts with the explicit Google button auth flow
  const isOnAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const isOnRedirectPage = location.pathname === '/redirect' || location.pathname === '/back-to-figma';
  useGoogleOneTap({
    clientId: GOOGLE_AUTH_CONFIG.CLIENT_ID,
    disabled: isLoggedIn || checkingToken || isOnAuthPage, // Don't show if already logged in, checking token, or on auth pages
  });

  // Register auth callbacks once
  useEffect(() => {
    if (!callbacksRegistered.current) {
      callbacksRegistered.current = true;

      registerAuthCallbacks(
        // onLogout
        () => {
          dispatch(logout());
        },
        // onWalletUpdate
        (wallets) => {
          dispatch(setWallets(wallets));
        },
        // onNavigate
        (route) => {
          navigate(route);
        }
      );

      console.log('✅ Auth callbacks registered');
    }
  }, [dispatch, navigate]);

  // Reset onboardingRedirected flag when user navigates back to root and showOnboarding is false
  // This allows re-checking onboarding if needed (e.g., after logout/login or manual navigation back)
  useEffect(() => {
    if ((location.pathname === '/' || location.pathname === '/landing') && !showOnboarding) {
      // Reset the flag when user is on landing page and not being redirected
      // This allows the onboarding check to happen again on next token validation if needed
      onboardingRedirected.current = false;
      console.log('Reset onboardingRedirected flag - user is on landing page');
    }
  }, [location.pathname, showOnboarding]);

  // Handle plugin redirect navigation
  const handlePluginRedirect = (redirectContext: { projectId?: string | null; conversationId?: string | null; mode?: string | null }) => {
    const { projectId, conversationId, mode = 'prototyper' } = redirectContext;

    if (projectId && conversationId) {
      navigate(`/projects/${projectId}?conversationId=${conversationId}&mode=${mode}`);
    } else if (projectId) {
      navigate(`/projects/${projectId}?mode=${mode}`);
    } else if (conversationId) {
      navigate(`/conversation/${conversationId}?mode=${mode}`);
    }
  };

  // Shared authentication success handler
  const handleAuthSuccess = () => {
    setCheckingToken(false);
    dispatch(setAppLoading(false));

    // Handle plugin redirect after authentication
    const redirectStr = localStorage.getItem('ideoz_plugin_redirect');
    if (redirectStr) {
      try {
        const redirect = JSON.parse(redirectStr);
        handlePluginRedirect(redirect);
        localStorage.removeItem('ideoz_plugin_redirect');
      } catch (error) {
        console.error('Error handling plugin redirect:', error);
        localStorage.removeItem('ideoz_plugin_redirect');
      }
    }
  };

  // Shared authentication error handler
  const handleAuthError = async (error: any) => {
    console.error('Error validating access token:', error);
    setCheckingToken(false);
    dispatch(setAppLoading(false));
    removeAccessToken();
    await handleVisitorCheck();
  };

  // Shared token validation function
  const validateToken = async (token: string) => {
    if (typeof window !== 'undefined') {
      (window as any)['appAccessToken'] = token;
    }
    saveAccessToken(token);

    try {
      // Use getUserProfile thunk which will check metadata.isOnBoardingShown and set showOnboarding accordingly
      const userData = await dispatch(getUserProfile(token)).unwrap();

      // Check if user is not on landing page and isOnBoardingShown is false
      // If so, call API to update the flag
      const pathname = window.location.pathname;
      const isOnLandingPage = pathname === '/' || pathname === '/landing';

      // Check if coming from Figma plugin (check URL params)
      const urlParams = new URLSearchParams(window.location.search);
      const figmaSource = urlParams.get('figma_source');
      const isFromPlugin = !!figmaSource;

      // Check both camelCase and snake_case, and handle string values
      const isOnBoardingShownRaw = userData?.metadata?.isOnBoardingShown ??
        userData?.metadata?.is_onboarding_shown ??
        false;
      const isOnBoardingShown = typeof isOnBoardingShownRaw === 'string'
        ? isOnBoardingShownRaw.toLowerCase() === 'true'
        : Boolean(isOnBoardingShownRaw);

      console.log('validateToken - onboarding check:', {
        isOnBoardingShown,
        isOnLandingPage,
        pathname,
        onboardingRedirected: onboardingRedirected.current,
        isFromPlugin,
        figmaSource,
        metadata: userData?.metadata,
      });

      // Skip onboarding redirect if coming from Figma plugin
      // The auth handlers will handle the redirect to /redirect page
      if (!isFromPlugin) {
        // Only redirect to onboarding if:
        // 1. isOnBoardingShown is false
        // 2. User is on landing page
        // 3. We haven't already redirected to onboarding (prevents repeated redirects when navigating back)
        if (isOnBoardingShown === false && isOnLandingPage && !onboardingRedirected.current) {
          // User is on landing page and onboarding not shown yet
          // Explicitly set showOnboarding = true and navigate to /onboarding
          console.log('Setting showOnboarding to true and navigating to /onboarding');
          onboardingRedirected.current = true; // Mark that we've redirected
          dispatch(setShowOnboarding(true));
          navigate('/onboarding');
        } else if (!isOnLandingPage && isOnBoardingShown === false) {
          // User is not on landing page and onboarding not shown yet - update flag silently
          authService.updateOnboardingShown().catch((error) => {
            console.error('Failed to update onboarding shown flag:', error);
          });
        }
      } else {
        console.log('Skipping onboarding redirect - user coming from Figma plugin');
      }

      handleAuthSuccess();
    } catch (error) {
      console.error('Error validating access token:', error);
      
      // Only logout for actual authentication errors, not network/server errors
      const isAuthError = 
        (error as any)?.response?.status === 401 || 
        (error as any)?.response?.status === 403 ||
        (error as any)?.code === 'INVALID_TOKEN' ||
        (error as any)?.message?.includes?.('unauthorized') ||
        (error as any)?.message?.includes?.('invalid token');
      
      if (isAuthError) {
        // Actual authentication error - logout user
        //await handleAuthError(error);
      } else {
        // Network/server error - keep user logged in but stop loading
        console.warn('Non-auth error during token validation, keeping user logged in');
        setCheckingToken(false);
        dispatch(setAppLoading(false));
        // Optionally show a toast notification about connection issues
      }
    }
  };

  // Handle visitor check (with duplicate prevention)
  const handleVisitorCheck = async () => {
    // Prevent duplicate calls
    if (visitorCheckCalled.current) {
      console.log('🚫 Visitor check already called, skipping duplicate');
      return;
    }

    visitorCheckCalled.current = true;

    try {
      await checkVisitor(dispatch);
      // Note: checkVisitor no longer sets showOnboarding - that's handled separately
    } catch (error) {
      console.error('Error checking visitor:', error);
      visitorCheckCalled.current = false; // Reset on error to allow retry
    }
  };

  // Handle no token scenario
  const handleNoToken = async () => {
    setCheckingToken(false);
    await handleVisitorCheck();
  };

  // Initialize web environment
  const initializeWeb = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const queryFromUrl = urlParams.get('q');
    const projectIdFromUrl = urlParams.get('projectId');
    const conversationIdFromUrl = urlParams.get('conversationId');
    const modeFromUrl = urlParams.get('mode');
    const sourceFromUrl = urlParams.get('source');
    let accessToken = tokenFromUrl || getAccessToken();

    // Store query parameter if present
    if (queryFromUrl?.trim()) {
      localStorage.setItem('ideoz_pending_query', queryFromUrl.trim());
    }

    // Handle plugin redirect
    const isPluginRedirect = sourceFromUrl === 'plugin' && (projectIdFromUrl || conversationIdFromUrl);

    if (isPluginRedirect && !accessToken) {
      // Store redirect context for navigation after authentication
      localStorage.setItem('ideoz_plugin_redirect', JSON.stringify({
        projectId: projectIdFromUrl,
        conversationId: conversationIdFromUrl,
        mode: modeFromUrl || 'prototyper',
      }));
    }

    // Clean URL parameters (security: remove sensitive data from URL)
    if (tokenFromUrl || queryFromUrl || (isPluginRedirect && !accessToken)) {
      const url = new URL(window.location.href);
      if (tokenFromUrl) url.searchParams.delete('token');
      if (queryFromUrl) url.searchParams.delete('q');
      if (isPluginRedirect && !accessToken) {
        // Remove plugin redirect params (context stored in localStorage)
        url.searchParams.delete('projectId');
        url.searchParams.delete('conversationId');
        url.searchParams.delete('source');
        // Keep mode in URL - pages read it directly if needed
      }
      window.history.replaceState({}, document.title, url.toString());
    }

    const storedOnboarding = localStorage.getItem('ideoz_onboarding_complete');

    if (accessToken) {
      // If already logged in and plugin redirect, navigate immediately
      // (navigation will create clean URL, no need to clean current one)
      if (isPluginRedirect && (projectIdFromUrl || conversationIdFromUrl)) {
        handlePluginRedirect({
          projectId: projectIdFromUrl,
          conversationId: conversationIdFromUrl,
          mode: modeFromUrl || 'prototyper',
        });
      }
      await validateToken(accessToken);
    } else {
      await handleNoToken();
    }

    // Handle onboarding
    if (storedOnboarding === 'true') {
      dispatch(setIsSkipOnBoarding(true));
    }

    // Set current app to 'Web' as default
    dispatch(setCurrentApp('Web'));
  };

  // Main initialization effect
  useEffect(() => {
    // Prevent duplicate initialization in React 18 Strict Mode
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    // Initialize Sentry on first mount
    initSentry();

    dispatch(setAppLoading(true));
    initializeWeb();
  }, []);

  const ispPage = location.pathname === '/p' || /^\/p\//.test(location.pathname);

  return (
    <>
      {isMobile && !ispPage && <MobileOnlyModal />}
      <Toaster position="top-right" expand={false} />

      {/* Subscription Expired Modal - Appears over any screen except redirect pages */}
      {!isOnRedirectPage && (
        <SmartPlanExpiredModal
          isOpen={subscriptionExpiredModal.isOpen}
          planName={subscriptionExpiredModal.planName}
          onRenewSubscription={subscriptionExpiredModal.onRenewSubscription}
          onProceedToDowngrade={subscriptionExpiredModal.onProceedToDowngrade}
          isProcessing={subscriptionExpiredModal.isProcessing}
          testId="smart-plan-expired-modal"
        />
      )}

      {/* Project Archive Modal - Appears when user with >1 project downgrades */}
      <ProjectArchiveModal
        isOpen={projectArchiveModal.isOpen}
        projects={projectArchiveModal.projects}
        onUpgradeInstead={projectArchiveModal.onUpgradeInstead}
        onKeepProject={projectArchiveModal.onKeepProject}
        isProcessing={projectArchiveModal.isProcessing}
        testId="project-archive-modal"
      />

      {/* Grace Period Modal - Appears for users with 2+ projects during grace period */}
      <GracePeriodModal
        isOpen={gracePeriodModal.isOpen}
        onClose={gracePeriodModal.onClose}
        onViewPlans={gracePeriodModal.onViewPlans}
        gracePeriodEndTime={gracePeriodModal.gracePeriodEndTime}
        isProcessing={gracePeriodModal.isProcessing}
        testId="grace-period-modal"
      />

      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/redirect" element={<Redirect />} />
        <Route path="/back-to-figma" element={<Redirect />} />
        <Route path="/oauth2-callback" element={<OAuth2Callback />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MyProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/myProfile"
          element={
            <ProtectedRoute>
              <MyProfile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          }
        />
        <Route path="/projects" element={<ProjectListPage />} />
        <Route path="/greeting" element={<UserGreating />} />
        <Route path="/conversation" element={<ConversationPage />} />
        <Route path="/conversation/:id" element={<ConversationPage />} />
        <Route path="/projects/:id" element={<ProjectDetails />} />
        <Route path="/p/*" element={<PublishedPrototype />} />
        <Route path="/canvas-demo" element={<CanvasExportDemo />} />
        {/* Catch all route - redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// Main App Component (wraps AppContent with BrowserRouter)
function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

// Export with Sentry Error Boundary
export default Sentry.withErrorBoundary(App, {
  fallback: (errorData) => (
    <div style={{ padding: '40px', textAlign: 'center', maxWidth: '600px', margin: '100px auto' }}>
      <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Something went wrong</h2>
      <p style={{ marginBottom: '24px', color: '#6b7280' }}>
        An unexpected error occurred. Please try refreshing the page.
      </p>
      <button
        onClick={errorData.resetError}
        style={{
          backgroundColor: '#3b82f6',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '6px',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
        }}
      >
        Try again
      </button>
      <details style={{ marginTop: '24px', textAlign: 'left' }}>
        <summary style={{ cursor: 'pointer', color: '#6b7280', fontSize: '14px' }}>
          Error details
        </summary>
        <pre
          style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#f3f4f6',
            borderRadius: '4px',
            fontSize: '12px',
            overflow: 'auto',
          }}
        >
          {errorData.error instanceof Error ? errorData.error.message : String(errorData.error)}
        </pre>
      </details>
    </div>
  ),
  beforeCapture: (scope, _error, errorInfo) => {
    scope.setTag('errorBoundary', true);
    scope.setContext('errorInfo', { errorInfo });
  },
});
